import { capitalize, useApi } from '@repo-level/core-front';
import { termsAndConditions } from 'api/cms/termsAndConditions';
import type { ModalDefaultProps } from 'components/Modal';
import { ModalTemplate } from 'components/ModalTemplate';
import type { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { Spinner } from 'components/Spinner';
import { ScStaticPage } from 'components/StaticModal/styled';
import { formatDateByLocale } from 'helpers/date';
import { i18nInstance } from 'i18n';
import { useMedia } from 'modules/common/hooks/useMedia';
import { usePoliciesByCountryCode } from 'modules/policies/hooks/usePoliciesByCountryCode';
import { forwardRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const TermsAndConditionsModal = forwardRef<
  HTMLDivElement,
  ModalDefaultProps
>(({ onClose }, ref) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('glossary');
  const { language } = i18nInstance;

  const { isMobileS } = useMedia();

  const [termsRequest, { pending, data }] = useApi(termsAndConditions);

  const licenseContent = usePoliciesByCountryCode(data?.licenseText);

  const modalSettings: ModalTemplateSettings = useMemo(
    () =>
      isMobileS
        ? { type: 'mobile', mode: { type: 'bottom' } }
        : {
            type: 'desktop',
            size: 'large',
            mode: { type: 'default' },
          },
    [isMobileS]
  );

  useEffect(() => {
    termsRequest(language);
  }, [termsRequest, language]);

  const formatDate = (date: string) => formatDateByLocale(date, locale);

  return (
    <>
      {pending ? (
        <Spinner absoluteCenter size={64} />
      ) : (
        data && (
          <ModalTemplate
            onClose={onClose}
            ref={ref}
            settings={modalSettings}
            title={data.titleH1}
          >
            <ScStaticPage>
              <div className="date">
                <p>
                  {capitalize(t('effectiveFrom'))}{' '}
                  <span>{formatDate(data.effectiveFrom)}</span>
                </p>
                <p>
                  {capitalize(t('lastUpdated'))}{' '}
                  <span>{formatDate(data.lastUpdated)}</span>
                </p>
                <p>
                  {capitalize(t('version'))} <span>{data.version}</span>
                </p>
              </div>
              <div dangerouslySetInnerHTML={{ __html: licenseContent }} />
            </ScStaticPage>
          </ModalTemplate>
        )
      )}
    </>
  );
});

export default TermsAndConditionsModal;
